import React, { useEffect, useState, useContext } from 'react';
import {
  Typography, Table, Tag, Button, Modal, Form, Select, Switch, Input, message, Space, Popconfirm, Divider,
} from 'antd';
import { EditOutlined, DeleteOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';

const ROLE_OPTIONS = [
  { label: 'Administrator',  value: 'admin'          },
  { label: 'QA Admin',       value: 'qa_admin'       },
  { label: 'QA User',        value: 'qa'             },
  { label: 'Support Agent',  value: 'support_agent'  },
  { label: 'Agent',          value: 'agent'          },
  { label: 'Client',         value: 'client'         },
];

const ROLE_COLOR = {
  admin:          'blue',
  qa_admin:       'purple',
  qa:             'cyan',
  support_agent:  'geekblue',
  agent:          'orange',
};
import api from '../api';
import { UserContext } from '../App';
import { formatTS } from '../utils/time';

import { selectFilter } from '../utils/selectFilter';
const ANALYTICS_TABS = [
  { label: 'Topics',               value: 'topics'    },
  { label: 'Topic Categories',     value: 'topic_cat' },
  { label: 'Reasons & Outcomes',   value: 'reasons'   },
  { label: 'Upselling',            value: 'upsell'    },
  { label: 'Save Attempts',        value: 'save'      },
  { label: 'Audio Issues',         value: 'audio'     },
  { label: 'Missed Opportunities', value: 'oppt'      },
  { label: 'Is Resolved',          value: 'resolved'  },
];

export default function AdminPage() {
  const currentUser = useContext(UserContext);
  const isAdmin        = currentUser?.Role === 'admin';
  // Edit, Lock and Add take User Management Access = Edit (the server
  // checks the same); the qa_admin role alone is not enough.
  const canEdit        = isAdmin || (currentUser?.CanEditUsers ?? 0) >= 2;
  // Impersonate: the Can Impersonate flag AND role admin or qa_admin (the
  // server applies the same rule). qa_admin cannot impersonate an admin or a
  // qa_admin; admin cannot impersonate another admin.
  const canImpersonate = (isAdmin || currentUser?.Role === 'qa_admin') && !!currentUser?.CanImpersonate;
  const mayImpersonate = (u) => canImpersonate && u.ID !== currentUser?.ID
    && !(isAdmin ? ['admin'] : ['admin', 'qa_admin']).includes(u.Role);
  const [users, setUsers]               = useState([]);
  // The grantable pages come from auth.PAGE_GRANTS via the API, so the list
  // here cannot drift from the keys the server actually checks.
  const [pageGrantOptions, setPageGrantOptions] = useState([]);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [allTeams, setAllTeams]         = useState([]);
  const [allJobs, setAllJobs]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [editUser, setEditUser]         = useState(null);
  const [createMode, setCreateMode]     = useState(false);
  const [modalOpen, setModalOpen]       = useState(false);
  const [saving, setSaving]             = useState(false);
  const [filterName, setFilterName]         = useState('');
  const [filterEmail, setFilterEmail]       = useState('');
  const [filterStatus, setFilterStatus]     = useState('active');
  const [filterRole, setFilterRole]         = useState('');
  const [filterTeam, setFilterTeam]         = useState(null);
  const [resetting, setResetting]           = useState(false);
  const [maintenance, setMaintenance]       = useState({ enabled: false, message: '', env_forced: false, timezone: 'America/New_York' });
  const [savingMaint, setSavingMaint]       = useState(false);
  const [form] = Form.useForm();

  const handleResetCases = async () => {
    setResetting(true);
    try {
      await api.delete('/api/admin/reset-cases');
      message.success('All evaluations cleared.');
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to clear evaluations.');
    } finally {
      setResetting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [usersRes, campaignsRes, teamsRes, maintRes, jobsRes] = await Promise.all([
        api.get('/api/admin/users'),
        api.get('/api/admin/all-campaigns'),
        api.get('/api/admin/teams'),
        api.get('/api/admin/maintenance'),
        api.get('/api/jobs').catch(() => ({ data: [] })),
      ]);
      setUsers(usersRes.data);
      setAllCampaigns(campaignsRes.data);
      setAllTeams(teamsRes.data);
      setMaintenance(maintRes.data);
      setAllJobs(jobsRes.data.filter((j) => j.IsActive));
    } catch {
      message.error('Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    api.get('/api/admin/page-grants')
      .then(r => setPageGrantOptions((r.data || []).map(p => ({ label: p.label, value: p.key }))))
      .catch(() => setPageGrantOptions([]));
  }, []);

  const openCreate = () => {
    setCreateMode(true);
    setEditUser(null);
    form.resetFields();
    form.setFieldsValue({
      role: 'agent', is_active: true, can_edit_evals: 0, can_config_evals: 0, can_edit_errors: 0, can_edit_incidents: 0, can_be_coach: false, can_edit_absences: 0, can_edit_formal_writeups: 0, can_edit_coaching: 0, can_edit_staff_history: 0, can_edit_users: 0,
      can_view_campaign_analytics: 0, can_view_ai_analytics: 0, can_view_oversight_stats: 0, can_view_recruitment_stats: 0, can_edit_forecasting: 0,
      can_search_calls: 0, can_view_agent_summary: 0, can_edit_engagements: 0, can_edit_schedule_variance: 0, can_edit_help_desk: 0, can_edit_clients: 0, can_edit_projects: 0, can_edit_campaign_updates: 0, can_edit_wfm_skills: 0, can_impersonate: false, is_view_only: false,
      can_view_financials: 0, can_assign_badge: 0,
      agent_nos: [], campaigns: [], analytics_tabs: [], visible_teams: [], also_teams: [], page_grants: [],
    });
    setModalOpen(true);
  };

  const openEdit = (user) => {
    setCreateMode(false);
    setEditUser(user);
    form.setFieldsValue({
      display_name:    user.DisplayName || '',
      email:           user.Email || '',
      role:            user.Role,
      is_active:       !!user.IsActive,
      is_locked:       !!user.IsLocked,
      can_edit_evals:    user.CanEditEvals ?? 0,
      can_config_evals:  user.CanConfigEvals ?? 0,
      can_edit_errors:     user.CanEditErrors ?? 0,
      can_edit_incidents:  user.CanEditIncidents ?? 0,
      can_be_coach:        !!user.CanBeCoach,
      can_edit_absences:        user.CanEditAbsences ?? 0,
      can_edit_formal_writeups: user.CanEditFormalWriteups ?? 0,
      can_edit_coaching:        user.CanEditCoaching ?? 0,
      can_edit_staff_history:        user.CanEditStaffHistory ?? 0,
      can_view_campaign_analytics:   user.CanViewCampaignAnalytics ?? 0,
      can_view_ai_analytics:         user.CanViewAIAnalytics ?? 0,
      can_view_oversight_stats:      user.CanViewOversightStats ?? 0,
      can_view_recruitment_stats:    user.CanViewRecruitmentStats ?? 0,
      can_edit_forecasting:          user.CanEditForecasting ?? 0,
      can_search_calls:              user.CanSearchCalls ?? 0,
      can_view_agent_summary:        user.CanViewAgentSummary ?? 0,
      can_edit_engagements:          user.CanEditEngagements ?? 0,
      can_edit_schedule_variance:    user.CanEditScheduleVariance ?? 0,
      can_edit_help_desk:            user.CanEditHelpDesk ?? 0,
      can_edit_clients:              user.CanEditClients ?? 0,
      can_edit_projects:             user.CanEditProjects ?? 0,
      can_edit_campaign_updates:     user.CanEditCampaignUpdates ?? 0,
      can_edit_wfm_skills:           user.CanEditWFMSkills ?? 0,
      can_impersonate:               !!user.CanImpersonate,
      can_assign_badge:              user.CanAssignBadge ?? 0,
      is_view_only:                  !!(user.IsViewOnly),
      can_edit_users:                user.CanEditUsers ?? 0,
      can_view_financials:           user.CanViewFinancials ?? 0,
      agent_nos:       user.AgentNos || [],
      payroll_id:      user.Payroll_ID || '',
      aws_id:          user.AWSID || '',
      team_id:         user.TeamID  || undefined,
      job_id:          user.JobID   || undefined,
      campaigns:       user.Campaigns || [],
      analytics_tabs:  user.AnalyticsTabs || [],
      page_grants:     user.PageGrants || [],
      visible_teams:   (user.VisibleTeams || []).map((t) => t.ID),
      also_teams:      (user.AlsoTeams || []).map((t) => t.ID),
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try { await form.validateFields(); } catch { return; }
    const values = form.getFieldsValue();
    if (values.role === 'support_agent') {
      if (!(values.visible_teams?.length)) {
        message.error('Support agents must have at least one Visible Team assigned.');
        return;
      }
      if (!(values.campaigns?.length)) {
        message.error('Support agents must have at least one Visible Campaign assigned.');
        return;
      }
    }
    setSaving(true);
    try {
      if (createMode) {
        await api.post('/api/admin/users', {
          display_name:   values.display_name,
          email:          values.email,
          role:           values.role,
          is_active:      values.is_active,
          can_edit_evals:    values.can_edit_evals,
          can_config_evals:  values.can_config_evals,
          can_edit_errors:     values.can_edit_errors,
          can_edit_incidents:  values.can_edit_incidents,
          can_be_coach:        values.can_be_coach,
          can_edit_absences:        values.can_edit_absences,
          can_edit_formal_writeups: values.can_edit_formal_writeups,
          can_edit_coaching:        values.can_edit_coaching,
          can_edit_staff_history:        values.can_edit_staff_history,
          can_view_campaign_analytics:   values.can_view_campaign_analytics,
          can_view_ai_analytics:         values.can_view_ai_analytics,
          can_view_oversight_stats:      values.can_view_oversight_stats,
          can_view_recruitment_stats:    values.can_view_recruitment_stats,
          can_edit_forecasting:          values.can_edit_forecasting,
          can_search_calls:              values.can_search_calls,
          can_view_agent_summary:        values.can_view_agent_summary,
          can_edit_engagements:          values.can_edit_engagements,
          can_edit_schedule_variance:    values.can_edit_schedule_variance,
          can_edit_help_desk:            values.can_edit_help_desk,
          can_edit_clients:              values.can_edit_clients,
          can_edit_projects:             values.can_edit_projects,
          can_edit_campaign_updates:     values.can_edit_campaign_updates,
          can_edit_wfm_skills:           values.can_edit_wfm_skills,
          ...(isAdmin ? { can_impersonate: !!values.can_impersonate } : {}),
          can_assign_badge:              values.can_assign_badge,
          is_view_only:                  values.is_view_only,
          can_edit_users:                values.can_edit_users,
          can_view_financials:           values.can_view_financials,
          agent_nos:         values.agent_nos || [],
          payroll_id:        values.payroll_id || null,
          aws_id:            values.aws_id || null,
          team_id:           values.team_id || null,
          job_id:            values.job_id  || null,
          campaigns:         values.campaigns || [],
          analytics_tabs:    values.analytics_tabs || [],
          page_grants:       values.page_grants || [],
          visible_teams:     values.visible_teams || [],
          also_teams:        values.also_teams || [],
        });
        message.success('User created. They will be linked to their Azure AD account on first login.');
      } else {
        await api.put(`/api/admin/users/${editUser.ID}`, {
          display_name:    values.display_name,
          email:           values.email || null,
          role:            values.role,
          is_active:       values.is_active,
          is_locked:       values.is_locked,
          can_edit_evals:    values.can_edit_evals,
          can_config_evals:  values.can_config_evals,
          can_edit_errors:     values.can_edit_errors,
          can_edit_incidents:  values.can_edit_incidents,
          can_be_coach:        values.can_be_coach,
          can_edit_absences:        values.can_edit_absences,
          can_edit_formal_writeups: values.can_edit_formal_writeups,
          can_edit_coaching:        values.can_edit_coaching,
          can_edit_staff_history:        values.can_edit_staff_history,
          can_view_campaign_analytics:   values.can_view_campaign_analytics,
          can_view_ai_analytics:         values.can_view_ai_analytics,
          can_view_oversight_stats:      values.can_view_oversight_stats,
          can_view_recruitment_stats:    values.can_view_recruitment_stats,
          can_edit_forecasting:          values.can_edit_forecasting,
          can_search_calls:              values.can_search_calls,
          can_view_agent_summary:        values.can_view_agent_summary,
          can_edit_engagements:          values.can_edit_engagements,
          can_edit_schedule_variance:    values.can_edit_schedule_variance,
          can_edit_help_desk:            values.can_edit_help_desk,
          can_edit_clients:              values.can_edit_clients,
          can_edit_projects:             values.can_edit_projects,
          can_edit_campaign_updates:     values.can_edit_campaign_updates,
          can_edit_wfm_skills:           values.can_edit_wfm_skills,
          ...(isAdmin ? { can_impersonate: !!values.can_impersonate } : {}),
          can_assign_badge:              values.can_assign_badge,
          is_view_only:                  values.is_view_only,
          can_edit_users:                values.can_edit_users,
          can_view_financials:           values.can_view_financials,
          agent_nos:         values.agent_nos || [],
          payroll_id:        values.payroll_id || null,
          aws_id:            values.aws_id || null,
          team_id:           values.team_id || null,
          job_id:            values.job_id  || null,
          campaigns:         values.campaigns,
          analytics_tabs:    values.analytics_tabs,
          page_grants:       values.page_grants || [],
          visible_teams:     values.visible_teams || [],
          also_teams:        values.also_teams || [],
        });
        message.success('User updated.');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleLock = async (record) => {
    const newLocked = !record.IsLocked;
    try {
      await api.put(`/api/admin/users/${record.ID}`, { is_locked: newLocked });
      message.success(newLocked ? `${record.DisplayName} locked.` : `${record.DisplayName} unlocked.`);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to update user.');
    }
  };

  const saveMaintenance = async (patch) => {
    const updated = { ...maintenance, ...patch };
    setMaintenance(updated);
    setSavingMaint(true);
    try {
      await api.post('/api/admin/maintenance', { enabled: updated.enabled, message: updated.message });
      message.success(updated.enabled ? 'Maintenance mode enabled.' : 'Maintenance mode disabled.');
    } catch {
      message.error('Failed to update maintenance mode.');
    } finally {
      setSavingMaint(false);
    }
  };

  const triTag = (v) => {
    if (v === 3) return <Tag color="purple">Edit Manager</Tag>;
    if (v === 2) return <Tag color="green">Edit</Tag>;
    if (v === 1) return <Tag color="blue">View</Tag>;
    return <Tag color="default">No Access</Tag>;
  };

  const columns = [
    ...(canEdit || canImpersonate ? [{
      title: '',
      key: 'actions',
      // First and pinned: the table scrolls a long way sideways.
      fixed: 'left',
      render: (_, record) => (
        <Space>
          {canEdit && (isAdmin || record.Role !== 'admin') && (
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>Edit</Button>
          )}
          {record.Role !== 'admin' && (
            <>
              {canEdit && (
              <Popconfirm
                title={record.IsLocked ? `Unlock ${record.DisplayName}?` : `Lock ${record.DisplayName}?`}
                description={record.IsLocked ? 'They will be able to sign in again.' : 'They will not be able to sign in.'}
                onConfirm={() => handleToggleLock(record)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  size="small"
                  // Red means the user IS locked (the state, not the action);
                  // the icon shows the state, the label what clicking does.
                  danger={!!record.IsLocked}
                  icon={record.IsLocked ? <LockOutlined /> : <UnlockOutlined />}
                >
                  {record.IsLocked ? 'Unlock' : 'Lock'}
                </Button>
              </Popconfirm>
              )}
              {mayImpersonate(record) && (
                <Button size="small" onClick={() => {
                  localStorage.setItem('impersonateUserId', record.ID);
                  localStorage.setItem('impersonateUserName', record.DisplayName || record.Email);
                  window.location.href = '/';
                }}>
                  Impersonate
                </Button>
              )}
            </>
          )}
        </Space>
      ),
    }] : []),
    {
      title: 'Name', dataIndex: 'DisplayName', key: 'DisplayName',
      // Frozen with the actions column, so the name stays in view while
      // scrolling across the permission columns.
      fixed: 'left', width: 190,
      sorter: (a, b) => (a.DisplayName || '').localeCompare(b.DisplayName || ''),
      defaultSortOrder: 'ascend',
      render: (v, record) => (
        <div>
          <div>{v}</div>
          {!record.LastLogin && <Tag color="orange" style={{ marginTop: 4 }}>Pending Login</Tag>}
        </div>
      ),
    },
    {
      title: 'Email', dataIndex: 'Email', key: 'Email',
      sorter: (a, b) => (a.Email || '').localeCompare(b.Email || ''),
      // The Azure OID under the email, for admins only. It is not a secret --
      // it is a directory identifier, not a credential -- but it is internal
      // plumbing and nobody below admin is troubleshooting a login with it.
      //
      // Shown raw, whatever it is. A pending- value means this row has never
      // been claimed and the Email beside it is what the sign-in must arrive
      // with for it to be; a GUID means it has been claimed. Substituting
      // words for the pending string would hide the one thing worth reading
      // when two rows exist for the same person.
      render: (v, r) => (
        <div style={{ lineHeight: 1.35 }}>
          <div>{v || <Typography.Text type="secondary">—</Typography.Text>}</div>
          {isAdmin && r.AzureOID && (
            <Typography.Text
              type="secondary"
              style={{ fontSize: 11, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
            >
              {r.AzureOID}
            </Typography.Text>
          )}
        </div>
      ),
    },
    {
      title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 140, ellipsis: true,
      sorter: (a, b) => (a.TeamName || '').localeCompare(b.TeamName || ''),
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Also works for', dataIndex: 'AlsoTeams', key: 'AlsoTeams', width: 160,
      render: (v) => ((v || []).length
        ? (v || []).map((t) => <Tag key={t.ID} color="geekblue">{t.Name}</Tag>)
        : <Typography.Text type="secondary">—</Typography.Text>),
    },
    {
      title: 'Job', dataIndex: 'JobName', key: 'JobName', width: 130, ellipsis: true,
      sorter: (a, b) => (a.JobName || '').localeCompare(b.JobName || ''),
      render: (v) => v ? <Tag color="geekblue">{v}</Tag> : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Agent No(s)', dataIndex: 'AgentNos', key: 'AgentNos', hidden: true,
      render: (v) => (v || []).length === 0
        ? <Typography.Text type="secondary">—</Typography.Text>
        : (v || []).map((no) => <Tag key={no}>{no}</Tag>),
    },
    {
      title: 'Role',
      dataIndex: 'Role',
      key: 'Role',
      sorter: (a, b) => (a.Role || '').localeCompare(b.Role || ''),
      render: (v) => {
        const opt = ROLE_OPTIONS.find((r) => r.value === v);
        return <Tag color={ROLE_COLOR[v] ?? 'default'}>{opt?.label ?? v}</Tag>;
      },
    },
    {
      title: 'Active',
      dataIndex: 'IsActive',
      key: 'IsActive',
      sorter: (a, b) => (b.IsActive ? 1 : 0) - (a.IsActive ? 1 : 0),
      render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Can Edit Evals',
      dataIndex: 'CanEditEvals',
      key: 'CanEditEvals',
      sorter: (a, b) => (a.CanEditEvals ?? 0) - (b.CanEditEvals ?? 0),
      render: triTag,
    },
    {
      title: 'Config Evals',
      dataIndex: 'CanConfigEvals',
      key: 'CanConfigEvals',
      sorter: (a, b) => (a.CanConfigEvals ?? 0) - (b.CanConfigEvals ?? 0),
      render: (v) => {
        if (v === 2) return <Tag color="green">Edit</Tag>;
        if (v === 1) return <Tag color="blue">View</Tag>;
        return <Tag color="default">No Access</Tag>;
      },
    },
    {
      title: 'Can Edit Errors',
      dataIndex: 'CanEditErrors',
      key: 'CanEditErrors',
      sorter: (a, b) => (a.CanEditErrors ?? 0) - (b.CanEditErrors ?? 0),
      render: triTag,
    },
    {
      title: 'Can Edit Incidents',
      dataIndex: 'CanEditIncidents',
      key: 'CanEditIncidents',
      sorter: (a, b) => (a.CanEditIncidents ?? 0) - (b.CanEditIncidents ?? 0),
      render: triTag,
    },
    {
      title: 'Can Be Coach',
      dataIndex: 'CanBeCoach',
      key: 'CanBeCoach',
      sorter: (a, b) => (b.CanBeCoach ? 1 : 0) - (a.CanBeCoach ? 1 : 0),
      render: (v) => <Tag color={v ? 'green' : 'default'}>{v ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Can Edit Coaching',
      dataIndex: 'CanEditCoaching',
      key: 'CanEditCoaching',
      sorter: (a, b) => (a.CanEditCoaching ?? 0) - (b.CanEditCoaching ?? 0),
      render: triTag,
    },
    {
      title: 'Can Edit Absences',
      dataIndex: 'CanEditAbsences',
      key: 'CanEditAbsences',
      sorter: (a, b) => (a.CanEditAbsences ?? 0) - (b.CanEditAbsences ?? 0),
      render: triTag,
    },
    {
      title: 'Schedule Variance',
      dataIndex: 'CanEditScheduleVariance',
      key: 'CanEditScheduleVariance',
      sorter: (a, b) => (a.CanEditScheduleVariance ?? 0) - (b.CanEditScheduleVariance ?? 0),
      render: triTag,
    },
    {
      title: 'Help Desk',
      dataIndex: 'CanEditHelpDesk',
      key: 'CanEditHelpDesk',
      sorter: (a, b) => (a.CanEditHelpDesk ?? 0) - (b.CanEditHelpDesk ?? 0),
      render: triTag,
    },
    {
      title: 'Clients',
      dataIndex: 'CanEditClients',
      key: 'CanEditClients',
      sorter: (a, b) => (a.CanEditClients ?? 0) - (b.CanEditClients ?? 0),
      render: triTag,
    },
    {
      title: 'Projects',
      dataIndex: 'CanEditProjects',
      key: 'CanEditProjects',
      sorter: (a, b) => (a.CanEditProjects ?? 0) - (b.CanEditProjects ?? 0),
      render: triTag,
    },
    {
      title: 'Campaign Updates',
      dataIndex: 'CanEditCampaignUpdates',
      key: 'CanEditCampaignUpdates',
      sorter: (a, b) => (a.CanEditCampaignUpdates ?? 0) - (b.CanEditCampaignUpdates ?? 0),
      render: triTag,
    },
    {
      title: 'WFM Skills',
      dataIndex: 'CanEditWFMSkills',
      key: 'CanEditWFMSkills',
      sorter: (a, b) => (a.CanEditWFMSkills ?? 0) - (b.CanEditWFMSkills ?? 0),
      render: triTag,
    },
    // Only role admin sees (and sets) Can Impersonate.
    ...(isAdmin ? [{
      title: 'Can Impersonate',
      dataIndex: 'CanImpersonate',
      key: 'CanImpersonate',
      sorter: (a, b) => Number(!!a.CanImpersonate) - Number(!!b.CanImpersonate),
      render: (v) => (v ? <Tag color="purple">Yes</Tag> : <span style={{ color: '#bfbfbf' }}>—</span>),
    }] : []),
    {
      title: 'Can Edit Writeups',
      dataIndex: 'CanEditFormalWriteups',
      key: 'CanEditFormalWriteups',
      sorter: (a, b) => (a.CanEditFormalWriteups ?? 0) - (b.CanEditFormalWriteups ?? 0),
      render: triTag,
    },
    {
      title: 'Staff History',
      dataIndex: 'CanEditStaffHistory',
      key: 'CanEditStaffHistory',
      sorter: (a, b) => (a.CanEditStaffHistory ?? 0) - (b.CanEditStaffHistory ?? 0),
      render: (v) => {
        if (v === 2) return <Tag color="green">Edit</Tag>;
        if (v === 1) return <Tag color="blue">View</Tag>;
        return <Tag color="default">No Access</Tag>;
      },
    },
    {
      title: 'Campaign Analytics',
      dataIndex: 'CanViewCampaignAnalytics',
      key: 'CanViewCampaignAnalytics',
      sorter: (a, b) => (a.CanViewCampaignAnalytics ?? 0) - (b.CanViewCampaignAnalytics ?? 0),
      render: (v) => v >= 1 ? <Tag color="blue">View</Tag> : <Tag color="default">No Access</Tag>,
    },
    {
      title: 'AI Analytics',
      dataIndex: 'CanViewAIAnalytics',
      key: 'CanViewAIAnalytics',
      sorter: (a, b) => (a.CanViewAIAnalytics ?? 0) - (b.CanViewAIAnalytics ?? 0),
      render: (v) => v >= 1 ? <Tag color="blue">View</Tag> : <Tag color="default">No Access</Tag>,
    },
    {
      title: 'Oversight Stats',
      dataIndex: 'CanViewOversightStats',
      key: 'CanViewOversightStats',
      sorter: (a, b) => (a.CanViewOversightStats ?? 0) - (b.CanViewOversightStats ?? 0),
      render: (v) => v >= 1 ? <Tag color="blue">View</Tag> : <Tag color="default">No Access</Tag>,
    },
    {
      title: 'Recruitment Stats',
      dataIndex: 'CanViewRecruitmentStats',
      key: 'CanViewRecruitmentStats',
      sorter: (a, b) => (a.CanViewRecruitmentStats ?? 0) - (b.CanViewRecruitmentStats ?? 0),
      render: (v) => v >= 1 ? <Tag color="blue">View</Tag> : <Tag color="default">No Access</Tag>,
    },
    {
      title: 'Forecasting',
      dataIndex: 'CanEditForecasting',
      key: 'CanEditForecasting',
      sorter: (a, b) => (a.CanEditForecasting ?? 0) - (b.CanEditForecasting ?? 0),
      render: (v) => {
        if (v === 2) return <Tag color="green">Edit</Tag>;
        if (v === 1) return <Tag color="blue">View</Tag>;
        return <Tag color="default">No Access</Tag>;
      },
    },
    {
      title: 'Search Calls',
      dataIndex: 'CanSearchCalls',
      key: 'CanSearchCalls',
      sorter: (a, b) => (a.CanSearchCalls ?? 0) - (b.CanSearchCalls ?? 0),
      render: (v) => v >= 1 ? <Tag color="blue">View</Tag> : <Tag color="default">No Access</Tag>,
    },
    {
      title: 'Agent Summary',
      dataIndex: 'CanViewAgentSummary',
      key: 'CanViewAgentSummary',
      sorter: (a, b) => (a.CanViewAgentSummary ?? 0) - (b.CanViewAgentSummary ?? 0),
      render: (v) => {
        if (v === 2) return <Tag color="green">View + Grade</Tag>;
        if (v === 1) return <Tag color="blue">View</Tag>;
        return <Tag color="default">No Access</Tag>;
      },
    },
    {
      title: 'Assign Badges',
      dataIndex: 'CanAssignBadge',
      key: 'CanAssignBadge',
      sorter: (a, b) => (a.CanAssignBadge ?? 0) - (b.CanAssignBadge ?? 0),
      render: (v) => (v ?? 0) >= 1 ? <Tag color="green">Yes</Tag> : <Tag color="default">No</Tag>,
    },
    {
      title: 'Engagements',
      dataIndex: 'CanEditEngagements',
      key: 'CanEditEngagements',
      sorter: (a, b) => (a.CanEditEngagements ?? 0) - (b.CanEditEngagements ?? 0),
      render: (v) => {
        if (v === 2) return <Tag color="green">Edit</Tag>;
        if (v === 1) return <Tag color="blue">View</Tag>;
        return <Tag color="default">No Access</Tag>;
      },
    },
    {
      title: 'View Only',
      dataIndex: 'IsViewOnly',
      key: 'IsViewOnly',
      width: 85,
      sorter: (a, b) => (a.IsViewOnly ?? 0) - (b.IsViewOnly ?? 0),
      render: (v) => v ? <Tag color="orange">Yes</Tag> : <Tag color="default">No</Tag>,
    },
    {
      title: 'User Mgmt',
      dataIndex: 'CanEditUsers',
      key: 'CanEditUsers',
      sorter: (a, b) => (a.CanEditUsers ?? 0) - (b.CanEditUsers ?? 0),
      render: (v) => {
        if (v === 2) return <Tag color="green">Edit</Tag>;
        if (v === 1) return <Tag color="blue">View</Tag>;
        return <Tag color="default">No Access</Tag>;
      },
    },
    {
      title: 'Visible Campaigns',
      dataIndex: 'Campaigns',
      key: 'Campaigns',
      render: (v) => (v || []).map((c) => <Tag key={c}>{c}</Tag>),
    },
    {
      title: 'Visible Teams',
      dataIndex: 'VisibleTeams',
      key: 'VisibleTeams',
      render: (v) => (v || []).length === 0
        ? <Typography.Text type="secondary">All teams</Typography.Text>
        : (v || []).map((t) => <Tag key={t.ID} color="geekblue">{t.Name}</Tag>),
    },
    {
      title: 'Analytics Tabs',
      dataIndex: 'AnalyticsTabs',
      key: 'AnalyticsTabs',
      render: (v) => (v || []).length === 0
        ? <Tag color="green">No Restrictions</Tag>
        : (v || []).map((t) => {
            const tab = ANALYTICS_TABS.find((a) => a.value === t);
            return <Tag key={t} color="purple">{tab?.label ?? t}</Tag>;
          }),
    },
    {
      title: 'Last Login', dataIndex: 'LastLogin', key: 'LastLogin',
      sorter: (a, b) => (a.LastLogin || '').localeCompare(b.LastLogin || ''),
      render: (v) => formatTS(v, 'YYYY-MM-DD') || '—',
    },
  ];

  return (
    <>
      <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
        User Management
      </Typography.Title>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Typography.Text type="secondary">
          Users are created automatically on first login, or you can pre-create them here.
        </Typography.Text>
        {canEdit && (
          <Button type="primary" icon={<EditOutlined />} onClick={openCreate}>
            New User
          </Button>
        )}
      </div>

      <Space wrap style={{ marginBottom: 12 }}>
        <Input
          placeholder="Filter by name…"
          allowClear
          style={{ width: 200 }}
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
        />
        <Input
          placeholder="Filter by email…"
          allowClear
          style={{ width: 220 }}
          value={filterEmail}
          onChange={(e) => setFilterEmail(e.target.value)}
        />
        <Select
          value={filterStatus}
          onChange={setFilterStatus}
          style={{ width: 130 }}
          options={[
            { label: 'Active',   value: 'active'   },
            { label: 'Inactive', value: 'inactive' },
            { label: 'All',      value: ''         },
          ]}
        />
        <Select
          allowClear
          placeholder="All roles"
          value={filterRole || undefined}
          onChange={(v) => setFilterRole(v || '')}
          style={{ width: 160 }}
          options={[...ROLE_OPTIONS, { label: 'All roles', value: '' }].filter((o) => o.value !== '')}
        />
        <Select
          allowClear
          showSearch filterOption={selectFilter}
          placeholder="All teams"
          value={filterTeam}
          onChange={(v) => setFilterTeam(v ?? null)}
          style={{ width: 180 }}
          options={allTeams.filter((t) => t.Status === 'active').map((t) => ({ label: t.Name, value: t.ID }))}
          optionFilterProp="label"
        />
      </Space>

      <Table
        rowKey="ID"
        dataSource={users.filter((u) => {
          const name     = filterName.trim().toLowerCase();
          const email    = filterEmail.trim().toLowerCase();
          const statusOk = !filterStatus
            || (filterStatus === 'active' ? !!u.IsActive : !u.IsActive);
          const roleOk   = !filterRole  || u.Role   === filterRole;
          const teamOk   = !filterTeam  || u.TeamID === filterTeam;
          return (!name  || (u.DisplayName ?? '').toLowerCase().includes(name))
              && (!email || (u.Email       ?? '').toLowerCase().includes(email))
              && statusOk && roleOk && teamOk;
        })}
        columns={columns}
        loading={loading}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100', '250'], showTotal: (total) => `${total} users` }}
      />

      {isAdmin && <Divider />}

      {isAdmin && (
        <>
          <Typography.Title level={5} style={{ marginBottom: 12 }}>Maintenance Mode</Typography.Title>
          {maintenance.env_forced && (
            <Typography.Text type="warning" style={{ display: 'block', marginBottom: 12 }}>
              Maintenance mode is forced ON via the MAINTENANCE_MODE environment variable. Disable it there to allow the toggle below to take effect.
            </Typography.Text>
          )}
          <Space direction="vertical" style={{ width: '100%', maxWidth: 500 }}>
            <Space>
              <Switch
                checked={maintenance.enabled}
                disabled={maintenance.env_forced || savingMaint}
                onChange={(checked) => saveMaintenance({ enabled: checked })}
                style={{ backgroundColor: maintenance.enabled ? '#ff4d4f' : undefined }}
              />
              <Typography.Text strong style={{ color: maintenance.enabled ? '#ff4d4f' : undefined }}>
                {maintenance.enabled ? 'Maintenance mode is ON — non-admins cannot log in' : 'Maintenance mode is OFF'}
              </Typography.Text>
            </Space>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Message shown to users</Typography.Text>
              <Input.TextArea
                rows={2}
                value={maintenance.message}
                onChange={(e) => setMaintenance((m) => ({ ...m, message: e.target.value }))}
                onBlur={() => saveMaintenance({})}
                disabled={maintenance.env_forced}
                style={{ marginTop: 4 }}
                placeholder="The application is currently under maintenance. Please check back soon."
              />
            </div>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Application Timezone</Typography.Text>
              <div style={{ marginTop: 4 }}>
                <Select
                  value={maintenance.timezone}
                  onChange={(tz) => {
                    setMaintenance((m) => ({ ...m, timezone: tz }));
                    api.post('/api/admin/maintenance', { enabled: maintenance.enabled, message: maintenance.message, timezone: tz })
                      .then(() => message.success('Timezone updated.'))
                      .catch(() => message.error('Failed to update timezone.'));
                  }}
                  style={{ width: 320 }}
                  options={[
                    { label: 'Eastern (EST/EDT) — America/New_York',   value: 'America/New_York'    },
                    { label: 'Central (CST/CDT) — America/Chicago',    value: 'America/Chicago'     },
                    { label: 'Mountain (MST/MDT) — America/Denver',    value: 'America/Denver'      },
                    { label: 'Mountain (no DST) — America/Phoenix',    value: 'America/Phoenix'     },
                    { label: 'Pacific (PST/PDT) — America/Los_Angeles',value: 'America/Los_Angeles' },
                    { label: 'UTC',                                     value: 'UTC'                 },
                  ]}
                />
              </div>
            </div>
          </Space>

          <Divider />
          <Typography.Title level={5} style={{ color: '#ff4d4f' }}>
            Dev Tools (Mock Mode Only)
          </Typography.Title>
          <Popconfirm
            title="Clear all evaluations?"
            description="This permanently deletes all evaluations and responses. Eval forms are NOT affected."
            onConfirm={handleResetCases}
            okText="Yes, clear evaluations"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />} loading={resetting}>
              Clear All Evaluations
            </Button>
          </Popconfirm>
        </>
      )}

      <Modal maskClosable={false}
        title={createMode ? 'New User' : `Edit — ${editUser?.DisplayName}`}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText="Save"
        width={520}
      >
        {!createMode && editUser && (
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            User ID: <strong>{editUser.ID}</strong>
          </Typography.Text>
        )}
        <Form form={form} layout="vertical" style={{ marginTop: 0 }}>
          <Form.Item
            label="Display Name"
            name="display_name"
            rules={[{ required: true, message: 'Display name is required' }]}
          >
            <Input placeholder="Full name" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Enter a valid email address' },
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>

          <Form.Item
            label="Role"
            name="role"
            rules={[{ required: true, message: 'Role is required' }]}
          >
            {/* Only an admin can make someone an admin (the server checks too). */}
            <Select options={isAdmin ? ROLE_OPTIONS : ROLE_OPTIONS.filter((o) => o.value !== 'admin')} />
          </Form.Item>

          <Form.Item label="Agent Number(s)" name="agent_nos" extra="Resolved from MaxAgentsCache via Payroll ID. Read-only.">
            <Select mode="tags" open={false} disabled suffixIcon={null} placeholder="—" tokenSeparators={[',']} />
          </Form.Item>

          <Form.Item label="Payroll ID" name="payroll_id" extra="Employee ID from the payroll system.">
            <Input placeholder="e.g. EMP-00123" />
          </Form.Item>
          <Form.Item label="AWS ID" name="aws_id" extra="Agent identifier AWS Amazon Connect">
            <Input placeholder="e.g. TSmith@0054N000005Dbc3@00D410000006xvV" allowClear />
          </Form.Item>

          <Form.Item
            label="Team"
            name="team_id"
            rules={[{ required: true, message: 'Team is required' }]}
          >
            <Select
              placeholder="Select a team"
              options={allTeams.filter((t) => t.Status === 'active').map((t) => ({ label: t.Name, value: t.ID }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>

          {/* Teams this person also works for besides their own: supervisors
              of those teams can see them on Agent Summary (routes/agent_summary
              list_agents, database.also_visible) and get their absence alerts
              (routes/absences _alert_absence_staff). */}
          <Form.Item label="Also works for" name="also_teams">
            <Select
              mode="multiple"
              showSearch filterOption={selectFilter}
              allowClear
              options={allTeams.filter((t) => t.Status === 'active').map((t) => ({ label: t.Name, value: t.ID }))}
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item
            label="Job"
            name="job_id"
            rules={[{ required: true, message: 'Job is required' }]}
            extra="Set automatically by the nightly sync. Override here if needed."
          >
            <Select
              placeholder="Select a job"
              options={allJobs.map((j) => ({ label: j.JobName, value: j.JobID }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item label="Active" name="is_active" valuePropName="checked">
            <Switch />
          </Form.Item>

          {/* Edit only: creating a user never sets a lock. */}
          {!createMode && (
            <Form.Item label="Locked" name="is_locked" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}

          <Form.Item label="Evaluations Access" name="can_edit_evals">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
              { value: 2, label: 'Edit' },
              { value: 3, label: 'Edit Manager' },
            ]} />
          </Form.Item>

          <Form.Item label="Eval Forms Config" name="can_config_evals">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
              { value: 2, label: 'Edit' },
            ]} />
          </Form.Item>

          <Form.Item label="Error Submission Access" name="can_edit_errors">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
              { value: 2, label: 'Edit' },
              { value: 3, label: 'Edit Manager' },
            ]} />
          </Form.Item>

          <Form.Item label="Compliance Incidents Access" name="can_edit_incidents">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
              { value: 2, label: 'Edit' },
              { value: 3, label: 'Edit Manager' },
            ]} />
          </Form.Item>

          <Form.Item
            label="Can Be a Coach"
            name="can_be_coach"
            valuePropName="checked"
            dependencies={['can_edit_coaching']}
            rules={[{
              validator: (_, value) => {
                if (value && !(form.getFieldValue('can_edit_coaching') >= 2)) {
                  return Promise.reject(new Error('Can Be a Coach requires Coaching Sessions Access (Edit or Edit Manager).'));
                }
                return Promise.resolve();
              },
            }]}
          >
            <Switch />
          </Form.Item>

          <Form.Item label="Coaching Sessions Access" name="can_edit_coaching" dependencies={['can_be_coach']} rules={[{
            validator: (_, value) => {
              if (form.getFieldValue('can_be_coach') && !(value >= 2)) {
                return Promise.reject(new Error('Must have Coaching Sessions Access (Edit or higher) when Can Be a Coach is enabled.'));
              }
              return Promise.resolve();
            },
          }]}>
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
              { value: 2, label: 'Edit' },
              { value: 3, label: 'Edit Manager' },
            ]} />
          </Form.Item>

          <Form.Item label="Absence Notifications Access" name="can_edit_absences">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
              { value: 2, label: 'Edit' },
              { value: 3, label: 'Edit Manager' },
            ]} />
          </Form.Item>

          <Form.Item label="Schedule Variance Log Access" name="can_edit_schedule_variance">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
              { value: 2, label: 'Edit' },
              { value: 3, label: 'Edit Manager' },
            ]} />
          </Form.Item>

          <Form.Item label="Help Desk Access" name="can_edit_help_desk">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
              { value: 2, label: 'Edit' },
              { value: 3, label: 'Edit Manager' },
            ]} />
          </Form.Item>

          <Form.Item label="Clients Access" name="can_edit_clients">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
              { value: 2, label: 'Edit' },
              { value: 3, label: 'Edit Manager' },
            ]} />
          </Form.Item>

          {/* View: uses Projects (menus; can be made a member or assigned) --
              what they see still comes from membership. Edit: also creates
              Folders. */}
          <Form.Item label="Projects Access" name="can_edit_projects">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
              { value: 2, label: 'Edit (create Folders)' },
            ]} />
          </Form.Item>

          {isAdmin && (
            <Form.Item label="Can Impersonate" name="can_impersonate" valuePropName="checked"
              extra="Only works with role admin or qa_admin.">
              <Switch />
            </Form.Item>
          )}

          <Form.Item label="Campaign Updates Access" name="can_edit_campaign_updates">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
              { value: 2, label: 'Edit' },
              { value: 3, label: 'Edit Manager' },
            ]} />
          </Form.Item>

          <Form.Item label="WFM Skills Access" name="can_edit_wfm_skills">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
              { value: 2, label: 'Edit' },
              { value: 3, label: 'Edit Manager' },
            ]} />
          </Form.Item>

          <Form.Item label="Formal Writeups Access" name="can_edit_formal_writeups">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
              { value: 2, label: 'Edit' },
              { value: 3, label: 'Edit Manager' },
            ]} />
          </Form.Item>

          <Form.Item label="Employee History Access" name="can_edit_staff_history">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
              { value: 2, label: 'Edit' },
            ]} />
          </Form.Item>

          <Form.Item label="Campaign Analytics Access" name="can_view_campaign_analytics">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
            ]} />
          </Form.Item>

          <Form.Item label="AI Analytics Access" name="can_view_ai_analytics">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
            ]} />
          </Form.Item>

          <Form.Item label="Oversight Stats Access" name="can_view_oversight_stats">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
            ]} />
          </Form.Item>

          <Form.Item label="Recruitment Stats Access" name="can_view_recruitment_stats">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
            ]} />
          </Form.Item>

          <Form.Item label="Forecasting Access" name="can_edit_forecasting">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
              { value: 2, label: 'Edit' },
            ]} />
          </Form.Item>

          <Form.Item label="Search Calls Access" name="can_search_calls">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
            ]} />
          </Form.Item>

          <Form.Item label="Agent Summary Access" name="can_view_agent_summary">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
              { value: 2, label: 'View with Grade' },
            ]} />
          </Form.Item>

          <Form.Item label="Engagements Access" name="can_edit_engagements">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
              { value: 2, label: 'Edit' },
            ]} />
          </Form.Item>

          <Form.Item label="Assign Badges" name="can_assign_badge">
            <Select options={[
              { value: 0, label: 'No' },
              { value: 1, label: 'Yes' },
            ]} />
          </Form.Item>

          <Form.Item label="View Only" name="is_view_only" valuePropName="checked">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>

          <Form.Item label="User Management Access" name="can_edit_users">
            <Select options={[
              { value: 0, label: 'No Access' },
              { value: 1, label: 'View' },
              { value: 2, label: 'Edit' },
            ]} />
          </Form.Item>

          {isAdmin && (
            <Form.Item label="Financial Data Access" name="can_view_financials">
              <Select options={[
                { value: 0, label: 'No Access' },
                { value: 1, label: 'Basic' },
                { value: 2, label: 'Management' },
                { value: 3, label: 'Executive' },
              ]} />
            </Form.Item>
          )}

          <Form.Item label="Visible Campaigns" name="campaigns">
            <Select
              mode="multiple"
              showSearch filterOption={selectFilter}
              allowClear
              placeholder="Select campaigns…"
              options={allCampaigns.map((c) => ({ label: c, value: c }))}
            />
          </Form.Item>

          <Form.Item label="Visible Teams" name="visible_teams" extra="Leave empty to allow access to all teams.">
            <Select
              mode="multiple"
              showSearch filterOption={selectFilter}
              allowClear
              placeholder="All teams (no restriction)…"
              options={allTeams.filter((t) => t.Status === 'active').map((t) => ({ label: t.Name, value: t.ID }))}
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item label="Analytics Tabs" name="analytics_tabs">
            <Select
              mode="multiple"
              allowClear
              placeholder="No analytics access…"
              options={ANALYTICS_TABS}
            />
          </Form.Item>

          {/* Grants only ever widen a page's existing rule, so leaving this
              empty takes nothing away from someone who has the page by role. */}
          <Form.Item
            label="Page Access"
            name="page_grants"
            extra="Grants access to these pages on top of whatever this user's role already allows."
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="No extra page access…"
              options={pageGrantOptions}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
